import { handleErrorDialog } from '@juvare/common-web-ui/DialogHelper';
import { allowSwitchUser, allowUserSendMessages } from '@juvare/contact-management/backend/features/permissions/shared';
import { SlideoutMenuItem as SlideoutMenuItemCM } from '@juvare/contact-management/ui/components/SlideoutMenuButton';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { ContactManagementConfigContext } from '@juvare/contact-management/ui/providers';
import { useUserClient } from '@juvare/platform-sdk-react/ui/hooks';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { AppContext } from '../Context';
import { useRequestBackgroundCheckDetails } from '../features/background-check/hooks';
import { useCredentialDetailsConfig } from './useCredentialDetailsConfig';
import { useSelectedPerson } from './useSelectedPerson';

export interface SlideoutMenuItem {
    label: string;
    onClick: () => void;
    'data-test'?: string;
}

export type SlideOutMenuItemWithId = SlideoutMenuItem & SlideoutMenuItemCM;

interface Result {
    menuItems: SlideoutMenuItem[];
    handlePersonIdSelect: (personId: string) => void;
    handlePersonIdReset: () => void;
    selectedPersonId?: string;
}

interface UseSlideoutParams {
    clearIdOnReset?: boolean;
}

export const useSlideout = ({ clearIdOnReset = false }: UseSlideoutParams = {}): Result => {
    const userState = useUserState();
    const { switchUserPrev } = React.useContext(AppContext);
    const { notificationConfig } = React.useContext(ContactManagementConfigContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const userClient = useUserClient();
    const { t } = useTranslation();
    const location = useLocation();
    const { person, handlePersonIdSelect, handlePersonIdReset: handlePersonIdResetDefault, selectedPersonId } = useSelectedPerson();

    const handlePersonIdReset = () => {
        if (clearIdOnReset) {
            searchParams.delete('id');
            setSearchParams(searchParams);
        }

        handlePersonIdResetDefault();
    };

    React.useEffect(() => {
        if (selectedPersonId) {
            handlePersonIdReset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.hash]);

    const { getBackgroundCheckMenuItems } = useRequestBackgroundCheckDetails();
    const credentialDetailsConfig = useCredentialDetailsConfig();

    const getMenuItems = (): SlideoutMenuItem[] => {
        if (!person) {
            return [];
        }

        const backgroundCheckMenuItems = getBackgroundCheckMenuItems(person);
        const credentialMenuItems = credentialDetailsConfig.getMenuItems(person);

        const menuItems: SlideoutMenuItem[] = [...backgroundCheckMenuItems, ...credentialMenuItems];

        if (allowSwitchUser(userState) && ['active', 'inactive'].includes(person.data.systemStatus) && !switchUserPrev) {
            menuItems.push({
                label: t('menuItems.switchToThisAccount'),
                onClick: () => {
                    userClient
                        .switchUser({
                            provider: 'switch',
                            user: person.columns.email,
                        })
                        .then((response) => {
                            window.location.assign(response.loginSuccessUrl);
                        })
                        .catch(handleErrorDialog);
                },
                'data-test': 'menu-item-switch-to-user',
            });
        }
        if (notificationConfig?.onAccountNotificationSend && person.data.systemStatus === 'active' && allowUserSendMessages(userState)) {
            menuItems.push({
                label: t('menuItems.sendNotification'),
                onClick: () => notificationConfig?.onAccountNotificationSend?.([person.id]),
                'data-test': 'menu-item-send-message',
            });
        }

        return menuItems;
    };

    return { menuItems: getMenuItems(), handlePersonIdSelect, handlePersonIdReset, selectedPersonId };
};
